import { z } from "zod";

/**
 * Storage keys are attacker-influenced (they derive from uploaded filenames), so
 * they are constrained to a flat, printable character set with no traversal
 * segments and no absolute paths. Anything that reaches the filesystem or a
 * render command must pass through here first.
 */
export const assetKeySchema = z
  .string()
  .min(1)
  .max(512)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/]*$/, "asset key contains unsupported characters")
  .refine((key) => !key.split("/").includes(".."), "asset key must not traverse directories")
  .refine((key) => !key.includes("//"), "asset key must not contain empty path segments");

/** A point expressed in normalized 0..1 coordinates so it survives any resolution change. */
export const pointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});

/** A rectangle in normalized 0..1 coordinates, clamped to stay inside the frame. */
export const rectSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().gt(0).max(1),
    height: z.number().gt(0).max(1),
  })
  .refine((r) => r.x + r.width <= 1.0001, "rectangle extends past the right edge")
  .refine((r) => r.y + r.height <= 1.0001, "rectangle extends past the bottom edge");

/** CSS colors are injected into inline styles, so only safe literal forms are accepted. */
export const colorSchema = z
  .string()
  .max(64)
  .regex(/^(#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\)|[a-zA-Z]{3,24})$/, "unsupported color value");

export const easingSchema = z.enum(["linear", "ease", "ease-in", "ease-out", "ease-in-out", "spring"]);

export const fpsSchema = z.union([z.literal(24), z.literal(25), z.literal(30), z.literal(50), z.literal(60)]);

export type Fps = z.infer<typeof fpsSchema>;

/**
 * The timeline rate to use for content measured at `framesPerSecond`.
 *
 * Rounds *up* to the next supported rate rather than to the nearest. Choosing a
 * rate below the content's own throws frames away permanently — a screen
 * recording of someone typing genuinely carries 60 distinct frames a second,
 * and halving that is visible as stutter on exactly the moments a product demo
 * exists to show. Spending the extra frames is the cheaper mistake.
 */
export function snapFps(framesPerSecond: number): Fps {
  const ladder: Fps[] = [24, 25, 30, 50, 60];
  if (!Number.isFinite(framesPerSecond) || framesPerSecond <= 0) return 30;
  return ladder.find((rate) => framesPerSecond <= rate + 1) ?? 60;
}

export type Rect = z.infer<typeof rectSchema>;
export type Point = z.infer<typeof pointSchema>;
export type Easing = z.infer<typeof easingSchema>;
